/**
 * Compound sync cursor (docs/08 §5).
 * Initial cursor is the documented pair — never the client clock.
 */

import type { SyncState, SyncedTableName } from '../schemas/local-only';

/** docs/08 §5 — initial cursor first half. */
export const INITIAL_CURSOR_UPDATED_AT = 'epoch';

/** docs/08 §5 — initial cursor second half (nil uuid). */
export const INITIAL_CURSOR_ID = '00000000-0000-0000-0000-000000000000';

export const PULL_PAGE_SIZE = 500;

export function initialSyncState(table: SyncedTableName): SyncState {
  return {
    table,
    lastUpdatedAt: INITIAL_CURSOR_UPDATED_AT,
    lastId: INITIAL_CURSOR_ID,
  };
}

/**
 * Wire value for PostgREST / Postgres. The documented token `epoch` is accepted
 * by Postgres; ISO form is used so JSON clients compare lexicographically safely.
 */
export function cursorUpdatedAtForQuery(lastUpdatedAt: string): string {
  if (lastUpdatedAt === INITIAL_CURSOR_UPDATED_AT) {
    return '1970-01-01T00:00:00.000Z';
  }
  return lastUpdatedAt;
}

export function advanceCursorFromRow(
  table: SyncedTableName,
  row: Record<string, unknown>,
): SyncState {
  const updatedAt = row['updated_at'];
  const id = row['id'];
  if (typeof updatedAt !== 'string' || updatedAt.length === 0) {
    throw new Error(`pull: row missing updated_at for ${table}`);
  }
  if (typeof id !== 'string' || id.length === 0) {
    throw new Error(`pull: row missing id for ${table}`);
  }
  return { table, lastUpdatedAt: updatedAt, lastId: id };
}

function asSortKey(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Sort key matching the SQL order: updated_at asc, id asc. */
export function comparePullRows(a: Record<string, unknown>, b: Record<string, unknown>): number {
  const au = asSortKey(a['updated_at']);
  const bu = asSortKey(b['updated_at']);
  if (au < bu) return -1;
  if (au > bu) return 1;
  const ai = asSortKey(a['id']);
  const bi = asSortKey(b['id']);
  if (ai < bi) return -1;
  if (ai > bi) return 1;
  return 0;
}

export function rowAfterCursor(
  row: Record<string, unknown>,
  cursor: Pick<SyncState, 'lastUpdatedAt' | 'lastId'>,
): boolean {
  const updatedAt = asSortKey(row['updated_at']);
  const id = asSortKey(row['id']);
  const cursorAt = cursorUpdatedAtForQuery(cursor.lastUpdatedAt);
  if (updatedAt > cursorAt) return true;
  if (updatedAt === cursorAt && id > cursor.lastId) return true;
  return false;
}
